import { get, set, isNil } from 'lodash';
import { STEP_PROGRESS } from './troConstants';
import { TRO_PASSING_LEVEL_STATUSES } from '../constants/book';
import { VR_ERROR_REASONS, VR_RESULT_STATUS } from '../constants/error';
import { AR_CI_URL } from '../constants/url';
import { TRO_PLAYLIST, JPLAYER_ACTION_KEYS, PROGRESS_START, PROGRESS_END, } from '../common/components/demoVideos/demoVideoConstants';

const { READING_BOOKS, ANSWERING_QUESTIONS } = TRO_PLAYLIST;
const { DURATION, CURRENT_TIME, CURRENT_PERCENT_RELATIVE } = JPLAYER_ACTION_KEYS;
const { ONBOARDING_PROGRESS_STEPS_COUNT, REMINDERS_STEPS_COUNT } = STEP_PROGRESS;

// if we don't find a such index then we return the last available card,
// it is useful when we switch cards using browser arrows, and current step index is not updated correctly
// FE: when we switch from reminders page to mic instructions
export const getFinalCurrentStepIndex = (currentStepIndex, cards) =>
    isNil(get(cards, currentStepIndex)) ? cards.length - 1 : currentStepIndex;

// `reminder` & `start reading` pages are included at the same progress step index
// getReminderStepIndex and getStartReadingStepIndex are duplicate to be consistent and be easy modified in the future is needed
export const getReminderStepIndex = stepsCount => stepsCount - REMINDERS_STEPS_COUNT;

export const getStepsCount = (isExternalURLSourcePresent, isGranted) => ONBOARDING_PROGRESS_STEPS_COUNT - [isGranted].filter(Boolean).length - (isExternalURLSourcePresent ? 1 : 0);

export const getVrStatus = result => get(result, 'vrr_result.status', VR_RESULT_STATUS.UNAVAILABLE);

export const getVrErrorReason = result => get(result, 'vrr_result.error_reason', null);

export const getVrScore = result => get(result, 'vrr_result.score', null);

export const getLatestPassingLevel = bookResult => {
    const passingLevel = bookResult.passing_level;
    const isPassingLevelNull = passingLevel === null;
    const vrStatus = getVrStatus(bookResult);
    const vrErrorReason = getVrErrorReason(bookResult);
    const inProgressStatuses = [VR_RESULT_STATUS.UNAVAILABLE, VR_RESULT_STATUS.READY];
    const failedStatus = VR_RESULT_STATUS.FAILED;
    const pendingStatus = VR_RESULT_STATUS.PENDING;

    if (isPassingLevelNull && inProgressStatuses.includes(vrStatus)) return TRO_PASSING_LEVEL_STATUSES.IN_PROGRESS;

    if (isPassingLevelNull && vrStatus === failedStatus && vrErrorReason !== VR_ERROR_REASONS.TIMEOUT) return TRO_PASSING_LEVEL_STATUSES.NOT_FOUND;

    if (isPassingLevelNull && (vrStatus === pendingStatus || vrStatus === failedStatus)) return TRO_PASSING_LEVEL_STATUSES.PENDING_RESPONSE;

    return passingLevel;
};

// eslint-disable-next-line camelcase
export const getActiveBookResults = ({ measure_result: { book_results } }) => {
    const activeResults = [];
    book_results.forEach(result => {
        const comprehensionResultScore = get(result, 'comprehension_result.score');
        if (result.book_reading_level && result.is_valid) {
            activeResults.push({
                readingLevel: result.book_reading_level,
                passingLevel: getLatestPassingLevel(result),
                bookId: result.book_id,
                vrStatus: getVrStatus(result),
                vrScore: getVrScore(result),
                vrErrorReason: getVrErrorReason(result),
                bookTitle: result.book_title,
                comprehensionScore: comprehensionResultScore,
                isBookPassed: result.passing_level === TRO_PASSING_LEVEL_STATUSES.PASS,
                responsesQuestions: result.responsesQuestions,
                isComprehensionComplete: comprehensionResultScore !== null,
            });
        }
    });

    return activeResults;
};

export const getBookResults = (bookResults, level, bookId) => {
    const hasBookResult = bookResults.find((result) => result.bookId === bookId);

    if (!hasBookResult) {
        return [
            ...bookResults,
            {
                bookId,
                readingLevel: level,
                passingLevel: TRO_PASSING_LEVEL_STATUSES.NOT_STARTED,
                vrStatus: VR_RESULT_STATUS.UNAVAILABLE,
                vrScore: null,
                vrErrorReason: null,
                isComprehensionComplete: false,
            },
        ];
    }

    return bookResults;
};

export const getSetMediaDemoVideoProgress = (state, action) => {
    const demoVideosProgress = { ...state.demoVideosProgress };
    const isReadingBooksVideoNext = action.media.demoVideoId === READING_BOOKS.ID;

    // handles when user switches between videos
    if (isReadingBooksVideoNext) {
        set(demoVideosProgress, `${ANSWERING_QUESTIONS.ID}.progress`, PROGRESS_START);
        set(demoVideosProgress, `${ANSWERING_QUESTIONS.ID}.currentTime`, PROGRESS_START);
    } else {
        set(demoVideosProgress, `${READING_BOOKS.ID}.progress`, PROGRESS_END);
        set(demoVideosProgress, `${READING_BOOKS.ID}.currentTime`, state.demoVideosProgress[READING_BOOKS.ID].duration);
    }

    return demoVideosProgress;
};

export const getSetJPlayerOptionsDemoVideoProgress = (state, action) => {
    const demoVideosProgress = { ...state.demoVideosProgress };

    if (action.key === CURRENT_PERCENT_RELATIVE && action.value !== PROGRESS_START) {
        set(demoVideosProgress, `${state.activeVideoId}.progress`, action.value);

        if (action.value === PROGRESS_END) {
            set(demoVideosProgress, `${state.activeVideoId}.watched`, true);
        }
    }

    if (action.key === DURATION) {
        set(demoVideosProgress, `${state.activeVideoId}.duration`, action.value);
    }

    if (action.key === CURRENT_TIME && action.value !== PROGRESS_START) {
        set(demoVideosProgress, `${state.activeVideoId}.currentTime`, action.value);
    }

    return demoVideosProgress;
};

export const getARUrl = (queryParams) => {
    const queryString = Object.keys(queryParams).map(key => `${key}=${queryParams[key]}`).join('&');
    return process.env.NODE_ENV === 'development' ?
        `${AR_CI_URL}/index.html?${queryString}` :
        `${window.amplify_lantern_config.arUrl}/index.html?${queryString}`;
};

export const redirectToAR = (queryParams) => {
    window.location = getARUrl(queryParams);
};

export const redirectToARwithError = (isBenchmarkModeEnabled) => {
    const queryParams = {
        hasTroError: true,
        benchmarkMode: isBenchmarkModeEnabled,
    };
    redirectToAR(queryParams);
    return null;
};

export const formatBookTitle = title => typeof title === 'string' && title.toLowerCase().split(' ').join('-');

export const setArPageTitle = (isExternalUrlSourcePresent, title) => {
    if (isExternalUrlSourcePresent) {
        document.title = title;
    }
};
